// The free text a made-to-order product engraves. Kept apart from the component rendering it so
// the cart can read the engraving without pulling a React component in.

import { getEngravingCharRange } from "@components/ProductPage/mto/mtoVariants";

export const ENGRAVING_MAX_LENGTH = 12;

// A character is dropped into a regex character class, so the few characters holding a meaning in
// there are escaped (`-` would otherwise open a range, `]` would close the class).
const escapeForCharClass = (char) => char.replace(/[\\\]^-]/g, "\\$&");

// Only letters and numbers can be engraved (no spaces), unless the setting whitelists more
// characters through `allowExtraChars` (e.g. `[".", " "]`), which the CMS can leave undefined.
// Products engraving a name (the nameplates) turn numbers off through `allowNumbers`.
export const sanitizeEngravingText = (
  value,
  maxLength = ENGRAVING_MAX_LENGTH,
  allowExtraChars = [],
  allowNumbers = true
) => {
  const extraChars = (Array.isArray(allowExtraChars) ? allowExtraChars : [])
    .filter((char) => typeof char === "string" && char.length > 0)
    .map(escapeForCharClass)
    .join("");
  const numbers = allowNumbers ? "0-9" : "";
  return (value ?? "")
    .replace(new RegExp(`[^A-Za-z${numbers}${extraChars}]`, "g"), "")
    .slice(0, maxLength);
};

// The font the customer picked, or the one the CMS marked as the default. Settings offering a
// single font never render the picker, so nothing ever flips `isSelected` on it and the only font
// on offer is the one being engraved.
export const getSelectedFont = (setting) => {
  const fonts = setting?.fonts ?? [];
  return fonts.find((f) => f.isSelected) ?? fonts[0] ?? null;
};

// The engraved text lives on `font.default`. Fonts that were never edited can miss it,
// so we fall back on the first font holding a value to keep the preview stable.
export const getEngravingText = (setting) => {
  const fonts = setting?.fonts ?? [];
  return getSelectedFont(setting)?.default ?? fonts.find((f) => f.default)?.default ?? "";
};

// What keeps a made-to-order engraving out of the cart. The reason is reported rather than a plain
// boolean so the Add to Cart button can spell out what has to be fixed.
export const ENGRAVING_ISSUE = {
  EMPTY: "empty",
  TOO_SHORT: "too_short",
  TOO_LONG: "too_long",
};

// The single engraving hides the input (a character is picked instead) and the initial necklace
// engraves the characters picked one by one, so neither is held back by a free text.
const TYPES_WITHOUT_ENGRAVING_INPUT = ["single_engraving", "initial_necklace"];

// A setting is only held back once its input is on screen: the back is engraved (and its input
// rendered) once paid for through the "Yes" variant. Products sizing the engraving through their
// variants ("4-6") are paid for a number of characters, so a text shorter or longer than what the
// customer picked is held back too. Variants carrying no character count (the "Yes"/"No" of the
// back engraving, a ring size) leave the length up to the setting alone.
const getSettingIssue = (setting, variant, charRange) => {
  if (!setting?.fonts?.length) return null;

  const isBack = setting.type === "back_engraving";
  if (!isBack && setting.type !== "font_engraving") return null;
  if (isBack && variant?.title !== "Yes") return null;

  const text = getEngravingText(setting);
  const issue = {
    isBack,
    length: text.length,
    min: charRange?.min ?? null,
    max: charRange?.max ?? null,
  };

  if (!text) return { ...issue, type: ENGRAVING_ISSUE.EMPTY };
  if (!charRange) return null;
  if (text.length < charRange.min) return { ...issue, type: ENGRAVING_ISSUE.TOO_SHORT };
  if (text.length > charRange.max) return { ...issue, type: ENGRAVING_ISSUE.TOO_LONG };
  return null;
};

export const getEngravingIssue = (settings, mtoType, variant) => {
  if (TYPES_WITHOUT_ENGRAVING_INPUT.includes(mtoType)) return null;

  const charRange = getEngravingCharRange(variant);

  return (
    Object.values(settings ?? {})
      .map((setting) => getSettingIssue(setting, variant, charRange))
      .find(Boolean) ?? null
  );
};

// The casing a font engraves in. `Snell` and `Bebas` have both cases and engrave the text as typed
// (e.g. "HeLLo"), `MiniScript` only has a capital first letter, the "Lower" cuts of a font only
// have lowercase glyphs, and everything else is engraved uppercase. The text is cased here rather
// than through `text-transform` so what is stored, previewed and sent to the workshop is the exact
// same string (`capitalize` would also leave "oKFkf" as "OKFkf").
const MIXED_CASE_FONTS = ["Snell", "Carol"];

export const formatEngravingText = (text, fontFamily) => {
  const value = text ?? "";
  if (MIXED_CASE_FONTS.includes(fontFamily)) return value;
  if (fontFamily?.includes("Lower")) return value.toLowerCase();
  if (fontFamily === "MiniScript" || fontFamily === "Sloop") {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return value.toUpperCase();
};

// The character the customer picked on a `single_char` setting. Only the active char type
// (Letters, Numbers, ...) holds the selection shown to the customer.
export const getSelectedChar = (setting) => {
  const chars = setting?.chars ?? [];
  const activeCharType = chars.find((c) => c.isSelected) ?? chars[0];
  return activeCharType?.list?.find((item) => item.isSelect)?.char ?? "";
};

// Selects `char` on the setting holding it. The char types (Letters, Numbers, ...) are mutually
// exclusive, so only the one carrying the character stays selected. A character the setting does
// not offer leaves it untouched.
export const selectCharInSetting = (setting, char) => {
  const charType = setting.chars?.find((c) => c.list?.some((item) => item.char === char))?.type;
  if (!charType) return setting;

  return {
    ...setting,
    chars: setting.chars.map((c) => ({
      ...c,
      isSelected: c.type === charType,
      list: c.list.map((item) => ({
        ...item,
        isSelect: c.type === charType && item.char === char,
      })),
    })),
  };
};

// Products offering no font choice engrave with the one their preview is drawn with, and the
// workshop still has to be told which one it is. The initial necklace curves its characters along
// the pendant with this one.
export const INITIAL_NECKLACE_FONT = {
  name: "InitialNeck",
  font_family: "InitialNeck, InitialNeck-Num",
};

// The fonts a shared link carries, in the order the settings offering a choice are declared
// (e.g. `Sloop_Block`). Settings shipping a single font never render a picker, so there is nothing
// to share for them and they are left out on both sides.
const hasFontPicker = (setting) => (setting?.fonts?.length ?? 0) > 1;

export const getFontParam = (settings) =>
  Object.values(settings ?? {})
    .filter(hasFontPicker)
    .map((setting) => getSelectedFont(setting)?.name ?? "")
    .join("_");

// The other side of `getFontParam`: the fonts are matched back to the settings by position, the
// same way `preview` matches the variants, and an unknown font leaves its setting untouched.
export const applyFontParam = (settings, fontParam) => {
  const fontNames = (fontParam ?? "").split("_").filter(Boolean);
  if (!fontNames.length) return settings;

  let pickerIndex = -1;
  return Object.fromEntries(
    Object.entries(settings).map(([settingId, setting]) => {
      if (!hasFontPicker(setting)) return [settingId, setting];

      pickerIndex += 1;
      const selected = setting.fonts.find((f) => f.name === fontNames[pickerIndex]);
      if (!selected) return [settingId, setting];

      return [
        settingId,
        {
          ...setting,
          fonts: setting.fonts.map((f) => ({
            ...f,
            isSelected: f.name === selected.name,
            // The text was cased for the font it was typed with, so it is re-cased for this one
            default: formatEngravingText(f.default, selected.font_family),
          })),
        },
      ];
    })
  );
};

// The characters a shared link carries, one per setting offering a character selection, in the
// order they are declared. Empty slots are kept as empty segments so positions are preserved.
const hasCharPicker = (setting) => (setting?.chars?.length ?? 0) > 0;

export const getCharParam = (settings) => {
  const chars = Object.values(settings ?? {})
    .filter(hasCharPicker)
    .map(getSelectedChar);
  return chars.some(Boolean) ? chars.join("_") : "";
};

// The other side of `getCharParam`: the characters are matched back to the settings by position,
// and an empty segment leaves its setting on whatever the CMS selected.
export const applyCharParam = (settings, charParam) => {
  const chars = (charParam ?? "").split("_");
  if (!chars.some(Boolean)) return settings;

  let pickerIndex = -1;
  return Object.fromEntries(
    Object.entries(settings).map(([settingId, setting]) => {
      if (!hasCharPicker(setting)) return [settingId, setting];

      pickerIndex += 1;
      const char = chars[pickerIndex];
      return [settingId, char ? selectCharInSetting(setting, char) : setting];
    })
  );
};

// What `?preview=` carries, whichever layout is customizing the product: the characters picked one
// by one (the single engraving, the initial necklace), the names of the variants selected (the
// default layout), or the name engraved (the nameplate).
// Every layout mirrors its own selection into the URL from an effect, so anything rendered before
// it (the alternatives) reads a URL one commit behind and has to build the value from the settings
// instead.
export const getPreviewParam = (settings) => {
  const allSettings = Object.values(settings ?? {});

  if (allSettings.some(hasCharPicker)) return getCharParam(settings);

  const variantNames = allSettings.flatMap((setting) =>
    (setting?.variants ?? []).filter((variant) => variant.isSelected).map((variant) => variant.name)
  );
  if (variantNames.length) return variantNames.join("_");

  return getEngravingText(allSettings.find((setting) => setting?.type === "font_engraving"));
};

// The engraved text a shared link carries, one per setting offering an engraving, in the order
// they are declared (e.g. the front then the back). Empty inputs are kept as empty segments so
// the position of each engraving is preserved.
const hasEngravingInput = (setting) => (setting?.fonts?.length ?? 0) > 0;

export const getEngravingParam = (settings) => {
  const texts = Object.values(settings ?? {})
    .filter(hasEngravingInput)
    .map(getEngravingText);
  return texts.some(Boolean) ? texts.join("_") : "";
};

// The other side of `getEngravingParam`: the text is capped and filtered exactly like the input
// caps it, then stored on every font so it survives a font change. A segment left empty (or
// holding nothing engravable) keeps whatever the CMS declared.
export const applyEngravingParam = (settings, engravingParam) => {
  const texts = (engravingParam ?? "").split("_");
  if (!texts.some(Boolean)) return settings;

  let inputIndex = -1;
  return Object.fromEntries(
    Object.entries(settings).map(([settingId, setting]) => {
      if (!hasEngravingInput(setting)) return [settingId, setting];

      inputIndex += 1;
      const text = formatEngravingText(
        sanitizeEngravingText(
          texts[inputIndex],
          setting.maxLength || ENGRAVING_MAX_LENGTH,
          setting.allowExtraChars
        ),
        getSelectedFont(setting)?.font_family
      );
      if (!text) return [settingId, setting];

      return [
        settingId,
        { ...setting, fonts: setting.fonts.map((f) => ({ ...f, default: text })) },
      ];
    })
  );
};
